import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CreateUpdateDraftNoteDto,
  DraftNoteDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";

import { DOMAIN_FORM_SUBMISSIONS } from "../domain";
import { ACTION_GET_FORM_DRAFT_NOTE } from "../GetFormDraftNote/useGetFormDraftNote";

import { roleGuard } from "@api";
import { submissionsApi } from "@api/services";

export const ACTION_CREATE_UPDATE_DRAFT_NOTE = "CREATE_UPDATE_DRAFT_NOTE";

const allowedRoles = [UserRole.Owner, UserRole.Manager, UserRole.Respondent];

const createUpdateDraftNote = async (
  params: CreateUpdateDraftNoteDto,
): Promise<DraftNoteDto> => {
  const response =
    await submissionsApi.submissionsControllerCreateUpdateDraftNote(params);

  return response.data;
};

export const useCreateUpdateDraftNote = (
  user: UserProfileDto | null,
  formId: string,
): {
  createUpdateDraftNote: (params: CreateUpdateDraftNoteDto) => void;
  createUpdateDraftNoteAsync: (
    params: CreateUpdateDraftNoteDto,
  ) => Promise<DraftNoteDto>;
  createUpdateDraftNoteEnabled: boolean;
  isSuccessCreateUpdateDraftNote: boolean;
  isPendingCreateUpdateDraftNote: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_FORM_SUBMISSIONS, ACTION_CREATE_UPDATE_DRAFT_NOTE],
    mutationFn: enabled ? createUpdateDraftNote : undefined,
    onSuccess(data) {
      const queryKey = [DOMAIN_FORM_SUBMISSIONS, ACTION_GET_FORM_DRAFT_NOTE, formId];
      const currentData = queryClient.getQueryData<DraftNoteDto[] | undefined>(
        queryKey,
      );

      queryClient.setQueryData(queryKey, () => {
        if (!currentData) return [];

        let dataUpdated = false;

        const newData = currentData.map((d) => {
          if (d.id === data.id) {
            dataUpdated = true;

            return data;
          }

          return d;
        });

        if (!dataUpdated) {
          newData.push(data);
        }

        return newData;
      });
    },
  });

  return {
    createUpdateDraftNote: mutate,
    createUpdateDraftNoteAsync: mutateAsync,
    createUpdateDraftNoteEnabled: enabled,
    isSuccessCreateUpdateDraftNote: isSuccess,
    isPendingCreateUpdateDraftNote: isPending,
  };
};
